import { useContext, useEffect, useRef, useState } from "react";
import useTranslator from "../../../hooks/useTranslator";
import LandLordContext from "../../../context/landlord/LandLordContext";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  formatPropertyAddress,
  getIdealistaListingRef,
  haveValue,
  toast,
} from "../../../helper/common";
import {
  Avatar,
  Badge,
  Button,
  Card,
  Checkbox,
  Icon,
  Notice,
  PageHeader,
  TextField,
} from "../../../components/design";
import "../../../components/Profiles/ProfileTable.css";
import "./idealistaImport.css";

// What kind of Idealista link was pasted: the agency page we need
// (idealista.com/pro/<agency>/), a single listing, or something else.
const checkIdealistaLink = (value) => {
  const link = String(value ?? "").trim();
  if (!link) return { kind: "empty" };

  const agency = link.match(
    /^(?:https?:\/\/)?(?:www\.)?idealista\.com\/pro\/([^/?#\s]+)/i
  );
  if (agency) {
    const slug = agency[1].toLowerCase();
    return {
      kind: "agency",
      slug,
      url: `https://www.idealista.com/pro/${slug}/`,
    };
  }

  const listing = link.match(
    /^(?:https?:\/\/)?(?:www\.)?idealista\.com\/(?:[a-z]{2}\/)?inmueble\/(\d+)/i
  );
  if (listing) {
    return {
      kind: "listing",
      id: listing[1],
      url: `https://www.idealista.com/inmueble/${listing[1]}/`,
    };
  }

  // Still typing the start of an Idealista link (e.g. "https://www.ideal"):
  // too early to call it wrong.
  const lower = link.toLowerCase();
  const typing = [
    "https://www.",
    "http://www.",
    "https://",
    "http://",
    "www.",
    "",
  ]
    .flatMap((start) =>
      ["pro/", "inmueble/"].map((path) => `${start}idealista.com/${path}`)
    )
    .some((full) => full.startsWith(lower));
  return { kind: typing ? "typing" : "invalid" };
};

// Server error text, or our own if it sent none (or not as text).
const getErrorText = (error, fallback) => {
  const message = error?.response?.data?.message;
  return typeof message === "string" && message ? message : fallback;
};

const formatPrice = (price) => {
  if (!haveValue(price) || Number.isNaN(Number(price))) return "-";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(Number(price));
};

// The pasted link next to an agency link, the wrong and right parts marked.
const LinkCompare = ({ wrongLabel, wrongLink, _t }) => (
  <div className="idealista-import__compare">
    <div className="idealista-import__compare-item">
      <span className="idealista-import__compare-label">
        <Icon name="close" size={14} />
        {wrongLabel}
      </span>
      <span className="idealista-import__compare-link">{wrongLink}</span>
    </div>
    <div className="idealista-import__compare-item">
      <span className="idealista-import__compare-label">
        <Icon name="check" size={14} />
        {_t("idealistaAgency")}
      </span>
      <span className="idealista-import__compare-link">
        https://www.idealista.com/<mark className="is-right">pro</mark>/
        {_t("idealistaExampleSlug")}/
      </span>
    </div>
  </div>
);

const AddIdealistaProperty = () => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const { workspaceId } = useParams();
  const [searchParams] = useSearchParams();
  const { bringPropertiesKelify, previewPropertiesKelify } =
    useContext(LandLordContext);

  // form -> searching -> review
  const [step, setStep] = useState("form");
  const [link, setLink] = useState(searchParams.get("link") ?? "");
  const [agency, setAgency] = useState(null);
  const [listings, setListings] = useState([]);
  const [selected, setSelected] = useState([]);
  const [importing, setImporting] = useState(false);
  // Ignores a search that finishes after the person went back to the form.
  const searchRun = useRef(0);

  const checked = checkIdealistaLink(link);
  const addressLang =
    localStorage.getItem("i18nextLng")?.toLowerCase() || "en-us";
  const homePath = haveValue(workspaceId) ? `/${workspaceId}/home` : `/home`;

  const steps = [
    _t("idealistaStepOne"),
    _t("idealistaStepTwo"),
    _t("idealistaStepThree"),
  ];

  const searchListings = async (agencyLink) => {
    const run = ++searchRun.current;
    setAgency({ name: agencyLink.slug, url: agencyLink.url });
    setStep("searching");

    const res = await previewPropertiesKelify({ username_url: agencyLink.url });
    if (run !== searchRun.current) return;

    if (res?.status === "success") {
      const found = res?.listings ?? [];
      setAgency(res?.agency);
      setListings(found);
      setSelected(found.map((listing) => listing.id));
      setStep("review");
    } else {
      toast.error(getErrorText(res, _t("failedToImportProperties")));
      setStep("form");
    }
  };

  // "Update" on Properties opens this page with ?link=<agency page>.
  useEffect(() => {
    const fromUrl = checkIdealistaLink(searchParams.get("link"));
    if (fromUrl.kind === "agency") searchListings(fromUrl);
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (checked.kind === "agency") searchListings(checked);
  };

  const backToForm = () => {
    searchRun.current += 1;
    setStep("form");
  };

  const addManually = () =>
    navigate(homePath, { state: { openAddManually: true } });

  const handleImport = async () => {
    try {
      setImporting(true);
      const res = await bringPropertiesKelify({
        username_url: agency?.url,
        selected_ids: selected,
      });
      if (res.status === "success") {
        toast.success(res?.imported + " " + _t("propertiesImported"));
        navigate(homePath);
      } else {
        toast.error(getErrorText(res, _t("failedToImportProperties")));
      }
    } catch (error) {
      toast.error(getErrorText(error, _t("failedToImportProperties")));
    } finally {
      setImporting(false);
    }
  };

  const toggleListing = (id, isOn) =>
    setSelected((prev) =>
      isOn ? [...prev, id] : prev.filter((selectedId) => selectedId !== id)
    );

  // "3 listings", "1 bed": the number is added here, the words come from
  // the translations.
  const withCount = (count, oneKey, otherKey) =>
    `${count} ${_t(Number(count) === 1 ? oneKey : otherKey)}`;

  const formatFeatures = (listing) => {
    const parts = [];
    if (Number(listing?.rooms) === 0) parts.push(_t("idealistaStudio"));
    else if (haveValue(listing?.rooms))
      parts.push(withCount(listing.rooms, "idealistaBed", "idealistaBeds"));
    if (haveValue(listing?.bathrooms))
      parts.push(
        withCount(listing.bathrooms, "idealistaBath", "idealistaBaths")
      );
    if (haveValue(listing?.size)) parts.push(`${listing.size} m²`);
    return parts.length ? parts.join(" · ") : "-";
  };

  const brandIcon = <Avatar src="/images/idealista-icon.svg" />;

  if (step === "searching") {
    return (
      <div className="main-page-wrapper idealista-import">
        <PageHeader title={_t("addPropertiesAndGetStarted")} />
        <Card className="idealista-import__searching">
          {brandIcon}
          <h1>
            {_t("idealistaSearchingFrom")} {agency?.name}
          </h1>
          <p>{_t("idealistaSearchingTime")}</p>
          <div
            className="idealista-import__progress"
            role="progressbar"
            aria-label={_t("loading")}
          >
            <span />
          </div>
          <Button variant="secondary" onClick={backToForm}>
            {_t("cancel")}
          </Button>
        </Card>
      </div>
    );
  }

  if (step === "review") {
    const allSelected =
      listings.length > 0 && selected.length === listings.length;

    return (
      <div className="main-page-wrapper idealista-import">
        <PageHeader
          title={_t("idealistaChooseListings")}
          description={
            listings.length > 0 &&
            `${_t("idealistaWeFound")} ${withCount(
              listings.length,
              "idealistaListingSingular",
              "idealistaListingPlural"
            )}. ${_t("idealistaAllSelectedHint")}`
          }
        />

        <Card className="idealista-import__agency">
          {brandIcon}
          <div className="idealista-import__agency-text">
            <strong>{agency?.name}</strong>
            <span>{agency?.url?.replace(/^https:\/\/www\./, "")}</span>
          </div>
          <Badge>
            {withCount(
              listings.length,
              "idealistaListingSingular",
              "idealistaListingPlural"
            )}
          </Badge>
          <Button
            size="sm"
            variant="secondary"
            iconStart={<Icon name="edit" size={14} />}
            onClick={backToForm}
          >
            {_t("idealistaChangeLink")}
          </Button>
        </Card>

        {listings.length === 0 ? (
          <Notice tone="warning" title={_t("idealistaNoListingsTitle")}>
            {_t("idealistaNoListingsText")}
          </Notice>
        ) : (
          <div className="profile-table-card idealista-import__table-card">
            <div className="profile-table-header">
              <Checkbox
                checked={allSelected}
                indeterminate={selected.length > 0 && !allSelected}
                onChange={(isOn) =>
                  setSelected(isOn ? listings.map((listing) => listing.id) : [])
                }
                label={`${_t("idealistaSelectAll")} · ${selected.length} ${_t(
                  "idealistaOf"
                )} ${listings.length}`}
              />
            </div>
            <div className="profile-table-scroll">
              <table className="profile-table idealista-import__table">
                <thead>
                  <tr>
                    <th className="idealista-import__check-col"></th>
                    <th>{_t("address")}</th>
                    <th>{_t("idealistaFeatures")}</th>
                    <th>{_t("rent")}</th>
                    <th>{_t("idealistaReference")}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {listings.map((listing) => {
                    const isOn = selected.includes(listing.id);
                    return (
                      <tr
                        key={listing.id}
                        className={isOn ? "" : "is-unselected"}
                        onClick={() => toggleListing(listing.id, !isOn)}
                      >
                        <td>
                          <Checkbox
                            checked={isOn}
                            onChange={(on) => toggleListing(listing.id, on)}
                            ariaLabel={listing.address}
                          />
                        </td>
                        <td>
                          <div className="idealista-import__listing">
                            {listing.thumbnail ? (
                              <img src={listing.thumbnail} alt="" />
                            ) : (
                              <span className="idealista-import__thumb">
                                <Icon name="home" size={16} />
                              </span>
                            )}
                            <div>
                              <strong>
                                {formatPropertyAddress(listing, addressLang) ||
                                  listing.address ||
                                  "-"}
                              </strong>
                              {haveValue(listing.town) && (
                                <span>{listing.town}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>{formatFeatures(listing)}</td>
                        <td>
                          <strong>{formatPrice(listing.price)}</strong>
                        </td>
                        <td>
                          {listing.source_url ? (
                            <a
                              href={listing.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {getIdealistaListingRef(
                                listing.source_id,
                                listing.source_url
                              ) ?? listing.id}
                            </a>
                          ) : (
                            (getIdealistaListingRef(
                              listing.source_id,
                              listing.source_url
                            ) ?? listing.id)
                          )}
                        </td>
                        <td>
                          {listing.inSkor && (
                            <Badge tone="neutral">
                              {_t("idealistaAlreadyInSkor")}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="idealista-import__footer">
          <Button
            variant="secondary"
            size="lg"
            onClick={() => navigate(homePath)}
          >
            {_t("cancel")}
          </Button>
          <Button
            size="lg"
            loading={importing}
            disabled={selected.length === 0}
            onClick={handleImport}
          >
            {_t("idealistaImport")}{" "}
            {withCount(
              selected.length,
              "idealistaPropertySingular",
              "idealistaPropertyPlural"
            )}
          </Button>
        </div>
      </div>
    );
  }

  const showListingHelp = checked.kind === "listing";
  const showInvalidHelp = checked.kind === "invalid";
  const browserSlug =
    checked.kind === "agency" ? checked.slug : _t("idealistaExampleSlug");

  return (
    <div className="main-page-wrapper idealista-import idealista-import--centered">
      <PageHeader
        title={_t("addPropertiesAndGetStarted")}
        description={_t("idealistaNoPropertiesLead")}
      />

      <div className="idealista-import__layout">
        <Card className="idealista-import__how">
          <h2 className="idealista-import__eyebrow">
            {_t("idealistaHowItWorks")}
          </h2>
          <ol className="idealista-import__steps">
            {steps.map((stepText, index) => (
              <li key={index}>
                <span className="idealista-import__step-number">
                  {index + 1}
                </span>
                <span>{stepText}</span>
              </li>
            ))}
          </ol>
        </Card>

        <Card className="idealista-import__card">
          <form onSubmit={handleSearch} className="idealista-import__form">
            <div className="idealista-import__brand">
              {brandIcon}
              <div>
                <h2>{_t("idealistaConnectAgency")}</h2>
                <p>{_t("idealistaConnectAgencyText")}</p>
              </div>
            </div>

            <TextField
              id="idealista-agency-link"
              name="idealista-agency-link"
              label={_t("idealistaAgencyPageLabel")}
              placeholder="https://www.idealista.com/pro/your-agency/"
              value={link}
              error={showListingHelp || showInvalidHelp}
              onChange={(e) => {
                setLink(e.target.value);
              }}
            />

            {showListingHelp && (
              <Notice tone="warning" title={_t("idealistaListingNotAgency")}>
                <ol className="idealista-import__fix-steps">
                  <li>{_t("idealistaFixStepOne")}</li>
                  <li>{_t("idealistaFixStepTwo")}</li>
                  <li>{_t("idealistaFixStepThree")}</li>
                </ol>
                <LinkCompare
                  _t={_t}
                  wrongLabel={_t("idealistaListing")}
                  wrongLink={
                    <>
                      https://www.idealista.com/
                      <mark className="is-wrong">inmueble</mark>/{checked.id}/
                    </>
                  }
                />
                <a
                  className="idealista-import__open-link"
                  href={checked.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {_t("idealistaOpenListing")}
                  <Icon name="arrowRight" size={14} />
                </a>
              </Notice>
            )}

            {showInvalidHelp && (
              <Notice tone="danger" title={_t("idealistaWrongLinkTitle")}>
                <ol className="idealista-import__fix-steps">
                  <li>{_t("idealistaFixStepOne")}</li>
                  <li>{_t("idealistaFixStepTwo")}</li>
                  <li>{_t("idealistaFixStepThree")}</li>
                </ol>
                <LinkCompare
                  _t={_t}
                  wrongLabel={_t("idealistaYourLink")}
                  wrongLink={<mark className="is-wrong">{link.trim()}</mark>}
                />
              </Notice>
            )}

            {!showListingHelp && !showInvalidHelp && (
              <div className="idealista-import__where">
                <p>{_t("idealistaWhereToFind")}</p>
                <div className="idealista-import__browser" aria-hidden="true">
                  <Icon name="link" size={16} />
                  <span className="idealista-import__browser-bar">
                    https://www.idealista.com/
                    <mark className="is-right">pro/{browserSlug}</mark>/
                  </span>
                </div>
              </div>
            )}

            <div className="idealista-import__actions">
              <div className="idealista-import__manual">
                <span>{_t("idealistaNotOnIdealista")}</span>
                <Button
                  size="sm"
                  variant="secondary"
                  iconStart={<Icon name="edit" size={14} />}
                  onClick={addManually}
                >
                  {_t("addManually")}
                </Button>
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={checked.kind !== "agency"}
              >
                {_t("idealistaSearchListings")}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default AddIdealistaProperty;
